import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/auth/AuthContext";
import { AUTH } from "@/constants/testIds";
import { X } from "lucide-react";
import { api } from "@/lib/api";
import PasswordField from "@/components/PasswordField";

export default function AuthModal({ open, onClose }) {
  const { t } = useLang();
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => {
    if (!open) {
      setError("");
      setName("");
      setEmail("");
      setPassword("");
      setMode("login");
      setForgotSent(false);
    }
  }, [open]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) {
      document.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    if (mode === "forgot") {
      try {
        await api.post("/auth/forgot-password", { email });
        setForgotSent(true);
      } catch (err) {
        if (err.response?.status === 404) {
          setError(t.auth.forgot_not_found);
        } else {
          setError(err.response?.data?.detail || "Error");
        }
      } finally {
        setLoading(false);
      }
      return;
    }
    const res = mode === "login" ? await login(email, password) : await register(name, email, password);
    setLoading(false);
    if (res.ok) onClose();
    else setError(res.error || "Error");
  };

  const handleGoogle = () => {
    window.location.href = "/api/auth/google/login";
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid={AUTH.modal}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-[#0a0a0a]/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 12, opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-white border border-[#0a0a0a] shadow-[8px_8px_0_0_rgba(10,10,10,1)]"
          >
            <button
              onClick={onClose}
              data-testid={AUTH.modalClose}
              className="absolute top-3 right-3 p-2 hover:bg-[#f5f5f5] transition-colors"
              aria-label="Close"
            >
              <X size={18} />
            </button>

            <div className="p-8 lg:p-10">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8a8a8a]">
                · kuryarti / auth
              </div>
              <h2 className="mt-3 font-mono font-bold tracking-tighter text-2xl text-[#0a0a0a]">
                {t.auth.welcome}
              </h2>
              <p className="mt-2 font-sans text-sm text-[#4a4a4a]">{t.auth.sub}</p>

              <button
                onClick={handleGoogle}
                data-testid={AUTH.google}
                className="mt-8 w-full flex items-center justify-center gap-3 px-4 py-3 border-2 border-[#0a0a0a] bg-white text-[#0a0a0a] font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#0a0a0a] hover:text-white transition-colors"
              >
                <GoogleIcon />
                {t.auth.continue_google}
              </button>

              <div className="my-6 flex items-center gap-3">
                <span className="flex-1 h-px bg-[#e5e5e5]" />
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8a8a8a]">
                  {t.auth.or}
                </span>
                <span className="flex-1 h-px bg-[#e5e5e5]" />
              </div>

              {mode === "forgot" ? (
                forgotSent ? (
                  <div className="space-y-4">
                    <p className="font-sans text-sm text-[#4a4a4a]">{t.auth.forgot_sent}</p>
                    <button
                      type="button"
                      onClick={() => { setMode("login"); setForgotSent(false); }}
                      className="w-full px-4 py-3 bg-[#0a0a0a] text-white font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#ff006e] transition-colors"
                    >
                      {t.auth.switch_login}
                    </button>
                  </div>
                ) : (
                  <form onSubmit={submit} className="space-y-4">
                    <Field tid={AUTH.email} label={t.auth.email} type="email" value={email} onChange={setEmail} required />
                    {error && (
                      <div data-testid={AUTH.error} className="font-mono text-xs text-[#ff006e]">
                        {error}
                      </div>
                    )}
                    <button
                      type="submit"
                      data-testid={AUTH.submit}
                      disabled={loading}
                      className="w-full px-4 py-3 bg-[#0a0a0a] text-white font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#ff006e] transition-colors disabled:opacity-50"
                    >
                      {loading ? t.auth.loading : t.auth.forgot_send}
                    </button>
                  </form>
                )
              ) : (
              <form onSubmit={submit} className="space-y-4">
                {mode === "register" && (
                  <Field
                    tid={AUTH.name}
                    label={t.auth.name}
                    value={name}
                    onChange={setName}
                    required
                  />
                )}
                <Field tid={AUTH.email} label={t.auth.email} type="email" value={email} onChange={setEmail} required />
                <PasswordField tid={AUTH.password} label={t.auth.password} value={password} onChange={setPassword} />
                {mode === "login" && (
                  <div className="text-right -mt-2">
                    <button
                      type="button"
                      onClick={() => { setMode("forgot"); setError(""); }}
                      className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#8a8a8a] hover:text-[#ff006e]"
                    >
                      {t.auth.forgot_link}
                    </button>
                  </div>
                )}

                {error && (
                  <div data-testid={AUTH.error} className="font-mono text-xs text-[#ff006e]">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  data-testid={AUTH.submit}
                  disabled={loading}
                  className="w-full px-4 py-3 bg-[#0a0a0a] text-white font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#ff006e] transition-colors disabled:opacity-50"
                >
                  {loading ? t.auth.loading : mode === "login" ? t.auth.login : t.auth.register}
                </button>
              </form>
              )}

              <div className="mt-6 font-mono text-[11px] uppercase tracking-[0.18em] text-[#8a8a8a] text-center">
                {mode === "login" ? t.auth.no_account : t.auth.have_account}{" "}
                <button
                  type="button"
                  data-testid={AUTH.switch}
                  onClick={() => setMode(mode === "login" ? "register" : "login")}
                  className="text-[#0a0a0a] underline underline-offset-4 hover:text-[#ff006e]"
                >
                  {mode === "login" ? t.auth.switch_register : t.auth.switch_login}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const Field = ({ tid, label, type = "text", value, onChange, required }) => (
  <label className="block">
    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8a8a8a] mb-2">{label}</div>
    <input
      data-testid={tid}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      className="w-full px-3 py-2.5 border border-[#0a0a0a] bg-white text-[#0a0a0a] font-sans focus:outline-none focus:border-[#ff006e] transition-colors"
    />
  </label>
);

const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.9 32.4 29.3 35.5 24 35.5c-6.4 0-11.5-5.1-11.5-11.5S17.6 12.5 24 12.5c3 0 5.7 1.1 7.8 2.9l5.7-5.7C33.8 6.6 29.1 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5c10.4 0 19.5-7.6 19.5-19.5 0-1.2-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 12.5 24 12.5c3 0 5.7 1.1 7.8 2.9l5.7-5.7C33.8 6.6 29.1 4.5 24 4.5 16.3 4.5 9.7 8.9 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 43.5c5 0 9.6-1.9 13-5l-6-5.1c-1.8 1.3-4.1 2.1-7 2.1-5.2 0-9.7-3.1-11.4-7.5l-6.5 5C9.6 39 16.2 43.5 24 43.5z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.4-2.4 4.5-4.6 5.9l6 5.1C40.7 36.3 43.5 30.5 43.5 24c0-1.2-.1-2.4-.4-3.5z" />
  </svg>
);
